import { t } from '../../utils/i18n';
import { selectLocation } from '../../modules/actions/locationActions';
import { SCREEN } from '../../utils/constants';
import {
    appUrl,
    form,
    isFlowForCalendar,
    locations,
    providers,
    selectedCodeNumber,
    selectedCountry,
    setLocationId,
    setProviderId,
    staffId,
    appointmentChannelId,
} from '../../utils/variables';
import { loadCountriesCodeDropdown } from '../components/countries';
import { loadLanguageDropdown } from '../components/languageDropdown';
import { maybeEnableRequestConsultButton, shouldSplitDobStep } from '../../modules/events/consultListeners';
import { renderDobDropdowns } from '../components/dobDropdowns';
import { shouldStepConsultFields } from '../../utils/qualificationVisibility';

export function loadRequestConsultComponents() {
    const shouldHideProviderSelection = isFlowForCalendar || (appointmentChannelId && form?.appointment_channel?.staffs?.length > 1);
    const isCategoryEnabled = form?.appointment_channel?.enable_category_selection;
    // 'hidden' consult provider mode: remove the provider field entirely. When a
    // default provider is configured the API returns just that one staff, so the
    // preselect block below still assigns it silently; with no default the field
    // is simply omitted and the consult is left unassigned.
    const hideConsultProviderField = (form?.consult_provider_mode || 'all_bookable') === 'hidden';
    if ((providers.length === 1 || staffId) && !shouldHideProviderSelection) {
        if (staffId) {
            setProviderId(+staffId);
            const provider = providers?.find(p => p.id === +staffId);
            setLocationId(selectLocation || provider?.location_ids[0]);
        } else {
            setProviderId(providers[0].id);
        }
    }

    // Language dropdown
    if (form?.show_language_dropdown) {
        loadLanguageDropdown();
    }

    // Native DOB/gender have two independent drivers:
    //  - calendar prebook flow: the appointment channel's collect_* flags (isPrebookPatientInfo)
    //  - consult REQUEST form: the widget's own show_dob_field / show_gender_field flags
    // (`appointmentChannelId` is only set for URL param or calendar flow, so on a plain
    // consult form only the widget-row flags apply.)
    const isPrebookPatientInfo = appointmentChannelId && form?.appointment_channel?.collect_patient_information_first_before_scheduling;

    // DOB and gender are deliberately NOT asked on the prebook patient-info
    // form: asking for them before the visitor has picked anything hurts
    // conversion, so on that flow BOTH drivers (channel `collect_dob` /
    // `collect_gender` and the widget-row `show_dob_field` /
    // `show_gender_field`) are gathered on the verify-info page right before
    // the booking is created instead (see appointmentItemsClickEventHandler).
    // Single-pager split: when the consult REQUEST form is the whole flow and
    // DOB is required, DOB is deferred to its own step AFTER the form submits
    // (lead already captured) instead of rendering inline — see
    // shouldSplitDobStep()/showConsultDobStep() in consultListeners.js.
    if (form?.show_dob_field && !isPrebookPatientInfo && !shouldSplitDobStep()) {
        requestAnimationFrame(() => {
            const host = document.querySelector('.requestConsult__container .dob__consult');
            if (!host || document.getElementById('dobConsult')) return;

            host.classList.remove('hidden');
            host.classList.add('form-control');
            host.setAttribute('data-form', 'dobConsult');

            renderDobDropdowns(host, {
                inputId: 'dobConsult',
                idPrefix: 'dob',
                onChange: maybeEnableRequestConsultButton,
            });
        });
    }

    if (form?.show_gender_field && !isPrebookPatientInfo) {
        requestAnimationFrame(() => {
            const host = document.querySelector('.requestConsult__container .gender__consult');
            if (!host || document.getElementById('genderConsult')) return;

            host.classList.remove('hidden');
            host.classList.add('form-control');
            host.setAttribute('data-form', 'genderConsult');

            host.innerHTML = `
                <label class="gender-dropdown-label">${t('gender')}</label>
                <select id="genderConsult" aria-label="${t('gender')}">
                    <option value="" disabled selected>${t('select_gender')}</option>
                    <option value="male">${t('male')}</option>
                    <option value="female">${t('female')}</option>
                    <option value="other">${t('other')}</option>
                    <option value="prefer_not_to_say">${t('prefer_not_to_say')}</option>
                </select>
            `;

            document.getElementById('genderConsult').addEventListener('change', () => {
                maybeEnableRequestConsultButton();
            });
        });
    }

    if (form?.show_insurance_field && !isPrebookPatientInfo) {
        requestAnimationFrame(() => {
            const host = document.querySelector('.requestConsult__container .insurance__consult');
            if (!host || document.getElementById('insuranceConsult')) return;

            host.classList.add('form-control');
            host.setAttribute('data-form', 'insuranceConsult');
            host.innerHTML = `
            <input
              id="insuranceConsult"
              name="insurance"
              type="text"
              placeholder="${t('insurance')}"
              autocomplete="off"
              aria-label="${t('insurance')}"
            />
          `;
        });
    }

    const consultFields = Array.isArray(form?.consult_request_fields)
        ? form.consult_request_fields
        : [];
    const requestForm = document.querySelector('.requestConsult__container form');
    const noteElement = document.querySelector('.requestConsult__container .requestConsult__a2p-consent') || document.querySelector('.requestConsult__container .requestConsult__simple-consent');
    const existingCustomFields = document.querySelector(
        '.requestConsult__container .custom-consult-fields'
    );
    if (existingCustomFields) {
        existingCustomFields.remove();
    }

    // Required lead-source dropdown — rendered only when the channel has
    // `lead_source_options` configured. Each option can be a string or an
    // object {value, label}; value is what gets sent as `leads.source`.
    const leadSourceOptions = Array.isArray(form?.appointment_channel?.lead_source_options)
        ? form.appointment_channel.lead_source_options
        : [];
    if (leadSourceOptions.length > 0 && !isFlowForCalendar && requestForm && noteElement) {
        const existingSource = document.querySelector('.requestConsult__container [data-form="sourceConsult"]');
        if (existingSource) existingSource.remove();

        const optVal = (o) => (typeof o === 'string' ? o : (o?.value ?? o?.label ?? String(o)));
        const optLabel = (o) => (typeof o === 'string' ? o : (o?.label ?? o?.value ?? String(o)));
        const sourceContainer = document.createElement('div');
        sourceContainer.className = 'form-control';
        sourceContainer.setAttribute('data-form', 'sourceConsult');
        sourceContainer.innerHTML = `
            <select id="sourceConsult" name="source" aria-label="${t('how_did_you_hear')}">
                <option value="" disabled selected>${t('how_did_you_hear')}</option>
                ${leadSourceOptions.map((o) => `<option value="${optVal(o)}">${optLabel(o)}</option>`).join('')}
            </select>
        `;
        requestForm.insertBefore(sourceContainer, noteElement);
        sourceContainer.querySelector('#sourceConsult').addEventListener('change', () => {
            maybeEnableRequestConsultButton();
        });
    }

    // Only add custom questions inline when ≤2 AND none carries a disqualifier;
    // otherwise they are shown one-per-page after submit (disqualifier fields
    // must be stepped so step/disqualified fires before the consult conversion).
    if (!isFlowForCalendar && consultFields.length > 0 && !shouldStepConsultFields(consultFields) && requestForm && noteElement) {
        const wrapper = document.createElement('div');
        wrapper.className = 'custom-consult-fields';
        consultFields.forEach((field) => {
            const fieldId = `customField__${field.id}`;
            const label = field.label || field.title || 'Custom field';
            const placeholder = field.placeholder || label;
            const options = field.answers || field.options || [];
            const optVal = (o) => (typeof o === 'string' ? o : (o?.value ?? o?.label ?? String(o)));
            const container = document.createElement('div');
            container.className = 'form-control';
            container.setAttribute('data-form', fieldId);

            switch (field.type) {
                case 'select': {
                    container.classList.add('consult-field--select');
                    const optionsHtml = options.map((o) => `<option value="${optVal(o)}">${optVal(o)}</option>`).join('');
                    container.innerHTML = `
                        <label class="consult-inline-label" for="${fieldId}">${label}</label>
                        <select id="${fieldId}" name="${fieldId}" aria-label="${label}">
                            <option value="" disabled selected>${t('select_an_option')}</option>
                            ${optionsHtml}
                        </select>
                    `;
                    break;
                }
                case 'textarea':
                    container.innerHTML = `
                        <textarea id="${fieldId}" name="${fieldId}" placeholder="${placeholder}" aria-label="${label}" rows="3"></textarea>
                    `;
                    break;
                case 'number':
                    container.innerHTML = `
                        <input id="${fieldId}" name="${fieldId}" type="number" min="0" placeholder="${placeholder}" aria-label="${label}" />
                    `;
                    break;
                case 'date':
                    container.innerHTML = `
                        <input id="${fieldId}" name="${fieldId}" type="text" class="dateQuestion--input consult-inline-date" placeholder="${placeholder}" readonly aria-label="${label}" />
                    `;
                    break;
                case 'checkbox':
                    container.classList.add('consult-field--checkbox');
                    container.innerHTML = `
                        <label class="consult-inline-label">${label}</label>
                        <div class="consult-inline-boolean-group">
                            <label class="consult-boolean-option" data-value="yes">
                                <input type="radio" name="${fieldId}_radio" value="yes" id="${fieldId}_yes" aria-label="${t('yes')}">
                                ${t('yes')}
                            </label>
                            <label class="consult-boolean-option" data-value="no">
                                <input type="radio" name="${fieldId}_radio" value="no" id="${fieldId}_no" aria-label="${t('no')}">
                                ${t('no')}
                            </label>
                        </div>
                        <input type="hidden" id="${fieldId}" name="${fieldId}" value="" />
                    `;
                    break;
                case 'multi_select': {
                    const optionsList = options.map((o) => {
                        const v = optVal(o);
                        return `<div class="option consult-inline-multi-option" data-option="${v}"><span class="option__name">${v}</span><span class="option__checkbox" data-option="${v}"><i class="option__icon-check ri-check-line"></i></span></div>`;
                    }).join('');
                    container.innerHTML = `
                        <label class="consult-inline-label">${label}</label>
                        <div class="list-question consult-inline-multi-select" data-field-id="${fieldId}">${optionsList}</div>
                        <input type="hidden" id="${fieldId}" name="${fieldId}" value="" />
                    `;
                    break;
                }
                case 'height':
                    container.innerHTML = `
                        <div class="heightQuestion consult-inline-height">
                            <input type="number" min="3" max="8" id="${fieldId}_feet" placeholder="${t('feet')}" aria-label="${t('feet')}" />
                            <span>${t('feet').toUpperCase()}</span>
                            <input type="number" min="0" max="11" id="${fieldId}_inch" placeholder="${t('inches')}" aria-label="${t('inches')}" />
                            <span>${t('inches').toUpperCase()}</span>
                        </div>
                        <input type="hidden" id="${fieldId}" name="${fieldId}" value="" />
                    `;
                    break;
                default:
                    container.innerHTML = `
                        <input id="${fieldId}" name="${fieldId}" type="text" placeholder="${placeholder}" autocomplete="off" aria-label="${label}" />
                    `;
            }

            wrapper.appendChild(container);
        });

        requestForm.insertBefore(wrapper, noteElement);
        initInlineConsultFields(consultFields.filter((f) => f.type === 'checkbox' || f.type === 'multi_select' || f.type === 'height' || f.type === 'date'));
    }

    if (locations.length > 1) {
        document.querySelector(
            '.requestConsult__container .location__dropdown'
        ).innerHTML = `
        <div data-form="locationConsult" class="locationsDropdown__wrapper dropdown form-control">
            <input id="locationConsult" autocomplete="off" placeholder="${t('select_location')}">
            <span class="down arrow">
                <i class="ri-arrow-down-s-line"></i>
            </span>
            <span class="up arrow">
                <i class="ri-arrow-up-s-line"></i>
            </span>
            <div class="locationsDropdown__select">
                <ul class="locations__select">
                </ul>
            </div>
        </div>
        `;
    }

    if (providers.length > 1 && !staffId && !isCategoryEnabled && !hideConsultProviderField) {
        document.querySelector(
            '.requestConsult__container .provider__dropdown'
        ).innerHTML = `<div data-form="providerConsult" class="providersDropdown__wrapper dropdown form-control">
                <div style="height: 100%;">
                    <input id="providerConsult" autocomplete="off" placeholder="${t('select_provider')}">
                    <img class="provider__selected-avt" >
                    <span class="provider__selected-title"></span>
                </div>
                <span class="down-arrow arrow" id="providerConsultDownArrow">
                    <i class="ri-arrow-down-s-line"></i>
                </span>
                <span class="up-arrow arrow" id="providerConsultUpArrow">
                    <i class="ri-arrow-up-s-line"></i>
                </span>
                <div class="providersDropdown__select">
                    <ul class="providers__select"></ul>
                </div>
            </div>`;
    }

    document
        .querySelector('.requestConsult__container .phone__selected-code img')
        .setAttribute(
            'src',
            `${appUrl}/assets/images/svg/${selectedCountry.toLowerCase()}.svg`
        );

    document.querySelector(
        '.requestConsult__container .selected-country-code'
    ).innerHTML = '+' + selectedCodeNumber;

    document.querySelector(
        '.requestConsult__container .phone__countryCode--select'
    ).innerHTML = loadCountriesCodeDropdown(SCREEN.REQUEST_CONSULT);
}

function initInlineConsultFields(fieldsWithSpecialHandling) {
    if (!fieldsWithSpecialHandling || fieldsWithSpecialHandling.length === 0) return;
    const container = document.querySelector('.requestConsult__container .custom-consult-fields');
    if (!container) return;

    fieldsWithSpecialHandling.forEach((field) => {
        const fieldId = `customField__${field.id}`;
        if (field.type === 'checkbox') {
            const hidden = document.getElementById(fieldId);
            const boolGroup = container.querySelector(`.consult-inline-boolean-group`);
            const formControl = container.querySelector(`[data-form="${fieldId}"]`);
            const optionEls = (formControl || boolGroup || container).querySelectorAll('.consult-boolean-option');
            optionEls.forEach((optEl) => {
                optEl.addEventListener('click', () => {
                    optionEls.forEach((o) => o.classList.remove('is-selected'));
                    optEl.classList.add('is-selected');
                    const radio = optEl.querySelector('input[type="radio"]');
                    if (radio) radio.checked = true;
                    if (hidden) {
                        hidden.value = optEl.dataset.value || '';
                        hidden.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            });
        } else if (field.type === 'multi_select') {
            const listEl = container.querySelector(`.consult-inline-multi-select[data-field-id="${fieldId}"]`);
            const hidden = document.getElementById(fieldId);
            if (!listEl || !hidden) return;
            const options = listEl.querySelectorAll('.consult-inline-multi-option');
            options.forEach((optEl) => {
                optEl.addEventListener('click', () => {
                    const val = optEl.getAttribute('data-option');
                    const checkbox = optEl.querySelector('.option__checkbox');
                    checkbox.classList.toggle('is-selected');
                    const selected = listEl.querySelectorAll('.option__checkbox.is-selected');
                    const arr = Array.from(selected).map((s) => s.getAttribute('data-option'));
                    hidden.value = JSON.stringify(arr);
                    hidden.dispatchEvent(new Event('change', { bubbles: true }));
                });
            });
        } else if (field.type === 'height') {
            const hidden = document.getElementById(fieldId);
            const feet = document.getElementById(`${fieldId}_feet`);
            const inch = document.getElementById(`${fieldId}_inch`);
            if (hidden && feet && inch) {
                const sync = () => {
                    const f = feet.value.trim();
                    const i = inch.value.trim();
                    hidden.value = f || i ? `${f || '0'} FT - ${i || '0'} IN` : '';
                    hidden.dispatchEvent(new Event('change', { bubbles: true }));
                };
                feet.addEventListener('input', sync);
                inch.addEventListener('change', sync);
            }
        } else if (field.type === 'date') {
            const input = document.getElementById(fieldId);
            if (input) {
                requestAnimationFrame(() => {
                    if (window.flatpickr) {
                        window.flatpickr(input, {
                            dateFormat: 'Y-m-d',
                            altInput: true,
                            altFormat: 'm/d/Y',
                        });
                    }
                });
            }
        }
    });
}
